import { faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Dialog,
  IconButton,
  makeStyles,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { endorsementCoverages } from 'data-layer-cr/dist/functions/coverages';
import {
  CatalogItem,
  CoverageCatalog,
  CoverageCatalogResume,
  Quotation,
  QuotationCoverage,
  QuotationUnit,
} from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { ButtonText, colors, moneyFormat } from 'design-system';
import { useEffect, useState } from 'react';
import { XCircle } from 'react-feather';
import {
  EndorsementComparison,
  IndividualCoveragesCard,
  IndividualDeductiblesCard,
  OptionPriceCard,
} from 'src/components/screens/Quotations/components';
import {
  isFlexibleOptionSelected,
  isPremiumOption,
  packageOptionsDescription,
} from 'src/functions/individualOptionHelpers';
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';
import { REQUEST_PRICING } from 'src/store/quotation/requestIdentifiers';
import { Plan } from '../../components/OptionPriceCard/SelectPlan';
import { DynamicEndorsementCard } from '../components';
import {
  filterPlatinumPLus,
  sortByPrice,
} from '../components/DynamicEndorsementCard/DynamicEndorsementCard';

const { formatMoney } = moneyFormat;

type IndividualOptionProps = {
  currentProfile: PricingProfileResponse;
  anualPrice: number;
  quotation: Quotation;
  coverages: CoverageCatalog[];
  coverageResumes: CoverageCatalogResume[];
  selectedEndorsement: QuotationCoverage;
  onEndorsementUpdated: (
    modifiedPackage: number,
    newCode: string,
    newValue: number,
  ) => void;
  onCustomerProfileUpdated: (
    modifiedPackage: number,
    newCode: string,
    newValue: number,
    packageSelected: string,
  ) => void;
  onEndorsementUpdatedExemption: (
    modifiedPackage: number,
    addExemptionCoverage: boolean,
    packageSelected: string,
  ) => void;
  unit: QuotationUnit;
  packageOption: number;
  savedQuotationUnit: QuotationUnit[];
  onChangeCoverageLimit: (
    modifiedPackage: number,
    modifiedCoverage: QuotationCoverage[],
    shouldUpdate: boolean,
    packageSelected: string,
  ) => void;
  onChangeDeductible: (
    modifiedPackage: number,
    deductibleOption: number,
  ) => void;
  plans: Plan[];
  loadingPackage?: number;
  onChangeLoadingPackage?: () => void;
  units: QuotationUnit[];
};

const useCustomStyles = makeStyles((theme: Theme) => {
  const nullBorder = 'none 0px transparent';
  return {
    divider: {
      width: '100%',
      borderTop: 'solid 1px #000000',
      opacity: '0.12',
      borderBottom: nullBorder,
      borderLeft: nullBorder,
      borderRight: nullBorder,
    },
    cardRoot: {
      marginTop: '1rem',
      position: 'relative',
      minWidth: 275,
      borderRadius: 10,
      overflow: 'hidden',
      backgroundColor: colors.white,
      boxShadow: '0px 0px 10px 0px #0000000D',
      border: '1px solid #EAEAEA',
    },
    triangle: {
      position: 'absolute',
      borderTop: '48.45px solid #E37E07',
      borderRight: '55.45px solid transparent',
    },
    star: {
      position: 'absolute',
      top: '5px',
      left: '4px',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      padding: '1.5rem',
    },
    coverageText: {
      fontSize: 17,
      fontWeight: 700,
      marginTop: '0.5rem',
    },
    optionContainer: ({ selectedPackage }: { selectedPackage: number }) => ({
      width: selectedPackage !== 2 ? '100%' : 'calc(100% - 35px)',
    }),
    coverageTextDisabled: { color: colors.light_silver },
    coverageValue: {
      fontSize: 19,
      fontWeight: 700,
      marginTop: '0.5rem',
    },
    toggleDialogue: {
      borderRadius: 10,
      [theme.breakpoints.down('md')]: {
        borderRadius: 0,
      },
    },
    closeIcon: {
      position: 'absolute',
      top: 14,
      right: theme.spacing(2),
      zIndex: 1101,
      [theme.breakpoints.down('sm')]: {
        position: 'fixed',
      },
    },
    titleText: {
      color: colors.white,
      [theme.breakpoints.down('sm')]: {
        color: colors.madison,
      },
    },
  };
});

const selectCoverage = (
  unit?: QuotationUnit,
  coverageId?: string,
): QuotationCoverage | undefined => {
  return unit?.coverages.find(
    (coverage: QuotationCoverage): boolean =>
      coverage.coverageId === coverageId,
  );
};

export const textDescriptionPlan = (
  packageOption: string,
  moneyType: string,
): string => {
  const options = packageOptionsDescription[moneyType as '₡' | '$'];
  if (packageOption === options.premium)
    return 'Máxima cobertura y más días de auto de alquiler';
  if (packageOption === options.economy)
    return 'Gran cobertura y beneficios básicos para tu protección';
  return 'Coberturas y beneficios básicos al mejor precio';
};

export const IndividualOption = ({
  currentProfile,
  anualPrice,
  quotation,
  coverages,
  coverageResumes,
  unit,
  selectedEndorsement,
  packageOption,
  savedQuotationUnit,
  onEndorsementUpdated,
  onChangeCoverageLimit,
  onChangeDeductible,
  onEndorsementUpdatedExemption,
  onCustomerProfileUpdated,
  plans,
  loadingPackage,
  onChangeLoadingPackage,
  units,
}: IndividualOptionProps): JSX.Element => {
  const customClasses = useCustomStyles({ selectedPackage: packageOption });
  const [toggleDialogue, setToggleDialogue] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [endorsement, setEndorsement] = useState(
    selectCoverage(unit, '0049') || selectCoverage(unit, '0040'),
  );

  const [currentEndorsement, setCurrentEndorsement] = useState(
    endorsement?.coverageId ?? '',
  );
  const [currentSelectedPlan, setCurrentSelectedPlan] = useState<string>(
    `${unit.package.description}/${currentEndorsement}`,
  );
  const flexibleOption = isFlexibleOptionSelected(
    currentSelectedPlan.split('/')[0],
    currentProfile.typeOfMoney,
  );
  const premiumOption = isPremiumOption(units, packageOption);
  const requestingQuotationState = useNetworkObserver(REQUEST_PRICING);

  useEffect(() => {
    setEndorsement(
      selectCoverage(unit, '0049') || selectCoverage(unit, '0040'),
    );
  }, [unit]);

  useEffect(() => {
    setCurrentSelectedPlan(`${unit.package.description}/${currentEndorsement}`);
  }, [currentEndorsement, unit.package.description]);

  const usedCoverageCatalogs = sortByPrice(
    endorsementCoverages(filterPlatinumPLus(coverages)),
  );

  const textClass = `${customClasses.coverageText} ${
    requestingQuotationState === 'requesting'
      ? customClasses.coverageTextDisabled
      : ''
  }`;

  const mappedValues: CatalogItem<CoverageCatalog>[] = usedCoverageCatalogs.map(
    (coverage: CoverageCatalog): CatalogItem<CoverageCatalog> => ({
      Value: coverage.code,
      Object: coverage,
      Text: (
        <Box
          justifyContent="space-between"
          alignItems="center"
          display="flex"
          style={{ width: '100%' }}
        >
          <Typography
            variant="caption"
            align="left"
            data-testid={`endoso-${coverage.code}`}
            className={textClass}
            style={{ fontSize: 15, fontWeight: 500 }}
          >
            {coverage.name}
          </Typography>
          <Typography
            variant="h2"
            data-testid={`coverageCostId-${coverage.code}`}
            className={customClasses.coverageValue}
            style={{ fontSize: 17, fontWeight: 400, color: '#4A4B4E' }}
          >
            {coverage.maxCost !== 0
              ? formatMoney(coverage.maxCost, false, currentProfile.typeOfMoney)
              : 'Gratis'}
          </Typography>
        </Box>
      ),
    }),
  );

  if (mappedValues.length <= 0) {
    return <div />;
  }

  const reversedMappedValues = mappedValues
    .filter((item) => item.Value)
    .slice(0)
    .reverse();

  const onChangeEndorsement = (value: string) => {
    onChangeLoadingPackage?.();
    setCurrentSelectedPlan(value);
    onCustomerProfileUpdated(
      packageOption,
      value.split('/')[1] || '',
      0,
      value.split('/')[0] || '',
    );
  };

  return (
    <Box
      className={customClasses.cardRoot}
      id={`card-${currentSelectedPlan.split('/')[0]}-${packageOption + 1}`}
    >
      <Dialog
        data-testid="dialogue"
        open={toggleDialogue}
        onClose={(): void => setToggleDialogue(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          className: customClasses.toggleDialogue,
        }}
      >
        <EndorsementComparison
          endorsements={reversedMappedValues}
          selectedEndorsement={currentSelectedPlan.split('/')[1] || ''}
          typeOfMoney={currentProfile.typeOfMoney}
        />
        <IconButton
          data-testid="close-button"
          className={customClasses.closeIcon}
          onClick={(): void => setToggleDialogue(false)}
        >
          <XCircle size={24} className={customClasses.titleText} />
        </IconButton>
      </Dialog>
      {premiumOption && (
        <>
          <Box className={customClasses.triangle} />
          <img
            src="/svg/premium_start.svg"
            alt="premium star"
            className={customClasses.star}
          />
        </>
      )}
      <Box className={customClasses.container}>
        <OptionPriceCard
          packageOption={packageOption}
          anualPrice={anualPrice}
          moneyType={currentProfile.typeOfMoney}
          plans={plans}
          onChangeEndorsementCustomerProfile={onChangeEndorsement}
          currentSelectedEndorsement={currentSelectedPlan}
          loadingPackage={loadingPackage}
        />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          style={{
            gap: '1rem',
            padding: '1rem',
            backgroundColor: '#F5FAFF',
            border: '1px solid #0193E5',
            borderRadius: '10px',
            marginTop: '0.5rem',
          }}
        >
          <Box display="flex" style={{ gap: '0.5rem' }} flex={1}>
            <FontAwesomeIcon
              style={{ marginTop: '5px' }}
              icon={faSparkles}
              size="lg"
              color={colors.bright_turquoise}
            />
            <Typography
              variant="h4"
              style={{ fontSize: '15px', fontWeight: 500 }}
            >
              {textDescriptionPlan(
                currentSelectedPlan.split('/')[0],
                currentProfile.typeOfMoney,
              )}
            </Typography>
          </Box>
          <ButtonText
            data-testid="comparison-button"
            style={{
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              width: 'fit-content',
              color: colors.bright_turquoise,
            }}
            onClick={(): void => setToggleDialogue(true)}
          >
            Ver más
          </ButtonText>
        </Box>
        <hr className={customClasses.divider} />
        <IndividualCoveragesCard
          data-testid="coverages-card"
          quotation={quotation}
          coverageCatalogs={coverages}
          coverageCatalogResumes={coverageResumes}
          unit={unit}
          shouldPersonalize={flexibleOption}
          anualPrice={anualPrice}
          onChangeCoverageLimit={onChangeCoverageLimit}
          packageOption={packageOption}
          savedQuotationUnit={savedQuotationUnit}
          onChangeExemptionCoverage={(newPackage, addExemptionCoverage) => {
            onEndorsementUpdatedExemption(
              newPackage,
              addExemptionCoverage,
              currentSelectedPlan.split('/')[0],
            );
          }}
          currentSelectedPlan={currentSelectedPlan.split('/')[0]}
          onChangeLoadingPackage={() => onChangeLoadingPackage?.()}
        />
        <hr className={customClasses.divider} />
        <IndividualDeductiblesCard
          data-testid="deductibles-card"
          coverageCatalogs={coverages}
          coverageCatalogResumes={coverageResumes}
          unit={unit}
          moneyType={currentProfile.typeOfMoney}
          shouldPersonalize={flexibleOption}
          packageOption={packageOption}
          onChangeDeductible={onChangeDeductible}
        />
        <hr className={customClasses.divider} />
        <DynamicEndorsementCard
          unit={unit}
          data-testid="endorsement-card"
          value={selectedEndorsement.coverageId}
          options={mappedValues}
          setCurrentEndorsement={setCurrentEndorsement}
          onChange={(newPackage, newCode, newValue) => {
            onEndorsementUpdated(newPackage, newCode, newValue);
          }}
          currentProfile={currentProfile}
          selectedPackage={packageOption}
          currentSelectedPlan={currentSelectedPlan.split('/')[0]}
          onChangeLoadingPackage={() => onChangeLoadingPackage?.()}
          typeOfMoney={currentProfile.typeOfMoney}
        />
      </Box>
    </Box>
  );
};

export default IndividualOption;
